/**
 * Unités canoniques (§2.2) :
 * - montants en CENTIMES (entiers) ;
 * - quantités en MILLIÈMES d'unité (entiers) ;
 * - TVA en POINTS DE BASE (2000 = 20 %) ;
 * - dates/horodatages en ISO 8601 UTC (suffixe Z), fuseau d'affichage Europe/Paris.
 */

export type Cents = number;
export type Milli = number;
export type BasisPoints = number;
export type IsoDateTime = string;
export type IsoDate = string;

/**
 * Taux de TVA français proposés sur les devis et factures (Q80) : normal, intermédiaire,
 * réduit, particulier (2,1 %) et exonéré. Les dépenses en proposent un sous-ensemble
 * (`EXPENSE_VAT_RATES_BP`) : une seule liste de référence, jamais recopiée.
 */
export const VAT_RATES_BP = [2000, 1000, 550, 210, 0] as const;
export type VatRateBp = (typeof VAT_RATES_BP)[number];

export function assertInt(n: number, label: string): void {
  if (!Number.isSafeInteger(n)) {
    throw new TypeError(`${label} doit être un entier sûr (reçu : ${n})`);
  }
}

/** Arrondi comptable « half-up » sur division entière (banquier refusé : règles FR). */
export function roundDiv(numerator: number, denominator: number): number {
  const q = numerator / denominator;
  return q >= 0 ? Math.floor(q + 0.5) : Math.ceil(q - 0.5);
}

/** HT (centimes) d'une ligne : prix unitaire (centimes) × quantité (millièmes). */
export function lineTotalCents(unitPriceCents: Cents, qtyMilli: Milli): Cents {
  assertInt(unitPriceCents, 'unitPriceCents');
  assertInt(qtyMilli, 'qtyMilli');
  return roundDiv(unitPriceCents * qtyMilli, 1000);
}

/** TVA (centimes) d'un montant HT au taux donné en points de base. */
export function vatCents(baseCents: Cents, vatBp: BasisPoints): Cents {
  assertInt(baseCents, 'baseCents');
  assertInt(vatBp, 'vatBp');
  return roundDiv(baseCents * vatBp, 10000);
}

export interface DocumentLine {
  unitPriceCents: Cents;
  qtyMilli: Milli;
  vatBp: BasisPoints;
}

/**
 * Règle d'arrondi du prix net remisé, figée sur chaque facture (migration 0204, lot BIL-2).
 * `exact` : la règle commune ci-dessous. `legacy_float` : l'ancienne règle,
 * `Math.round(prix × (1 − remise))` en virgule flottante, celle avec laquelle les factures
 * enregistrées avant la migration ont leurs totaux ; elle sert à relire ces factures
 * (avoir total, export comptable, Pennylane, PDF, écran) sans écart avec leurs totaux stockés.
 */
export const LINE_ROUNDING_RULES = ['exact', 'legacy_float'] as const;
export type LineRounding = (typeof LINE_ROUNDING_RULES)[number];

/** Règle d'une facture lue en base ; une valeur absente vaut la règle commune. */
export function lineRoundingOf(value: unknown): LineRounding {
  return value === 'legacy_float' ? 'legacy_float' : 'exact';
}

/**
 * Prix unitaire net de la remise de ligne : arrondi comptable (`roundDiv`) du prix
 * × (1 − remise). Une seule règle pour l'écran, le PDF, le Factur-X, Pennylane et les
 * analyses ; un prix négatif (avoir) s'arrondit symétriquement (−x,5 → −(x+1)).
 * `legacy_float` reproduit l'ancienne règle pour relire une facture enregistrée avec elle.
 */
export function netUnitPriceCents(
  unitPriceCents: Cents,
  discountBp: BasisPoints = 0,
  rounding: LineRounding = 'exact',
): Cents {
  assertInt(unitPriceCents, 'unitPriceCents');
  assertInt(discountBp, 'discountBp');
  if (rounding === 'legacy_float') return Math.round(unitPriceCents * (1 - discountBp / 10000)) + 0;
  return roundDiv(unitPriceCents * (10000 - discountBp), 10000);
}

/** Ligne saisie d'un devis ou d'une facture : prix brut et remise de ligne. */
export interface DiscountedLine extends DocumentLine {
  discountBp?: BasisPoints | undefined;
}

/** HT (centimes) d'une ligne remisée : prix net × quantité, avec les arrondis des totaux. */
export function discountedLineTotalCents(
  line: Pick<DiscountedLine, 'unitPriceCents' | 'qtyMilli' | 'discountBp'>,
  rounding: LineRounding = 'exact',
): Cents {
  return lineTotalCents(netUnitPriceCents(line.unitPriceCents, line.discountBp ?? 0, rounding), line.qtyMilli);
}

export interface DocumentTotals {
  totalHtCents: Cents;
  totalVatCents: Cents;
  totalTtcCents: Cents;
  /** Ventilation par taux (clé = points de base). */
  vatBreakdown: Record<number, { baseCents: Cents; vatCents: Cents }>;
}

/**
 * Totaux d'un document : TVA calculée PAR TAUX sur les bases cumulées
 * (règle fiscale FR : ventilation par taux, pas par ligne sommée).
 */
export function computeTotals(
  lines: readonly DocumentLine[],
  options: { reverseCharge?: boolean } = {},
): DocumentTotals {
  const byRate = new Map<number, number>();
  for (const l of lines) {
    const ht = lineTotalCents(l.unitPriceCents, l.qtyMilli);
    byRate.set(l.vatBp, (byRate.get(l.vatBp) ?? 0) + ht);
  }
  let totalHt = 0;
  let totalVat = 0;
  const vatBreakdown: DocumentTotals['vatBreakdown'] = {};
  for (const [bp, base] of byRate) {
    // TVA autoliquidée : le taux reste affiché, mais le client déclare la TVA.
    const v = options.reverseCharge ? 0 : vatCents(base, bp);
    vatBreakdown[bp] = { baseCents: base, vatCents: v };
    totalHt += base;
    totalVat += v;
  }
  return {
    totalHtCents: totalHt,
    totalVatCents: totalVat,
    totalTtcCents: totalHt + totalVat,
    vatBreakdown,
  };
}

/** Totaux d'un document dont les lignes portent une remise (prix net par `netUnitPriceCents`). */
export function computeDiscountedTotals(
  lines: readonly DiscountedLine[],
  options: { reverseCharge?: boolean; rounding?: LineRounding | undefined } = {},
): DocumentTotals {
  return computeTotals(
    lines.map((line) => ({
      unitPriceCents: netUnitPriceCents(line.unitPriceCents, line.discountBp ?? 0, options.rounding),
      qtyMilli: line.qtyMilli,
      vatBp: line.vatBp,
    })),
    { reverseCharge: options.reverseCharge ?? false },
  );
}

/** teqCO₂ recalculé, jamais stocké comme vérité (§9.3) : charge (g) × GWP / 1e6. */
export function teqCo2FromCharge(chargeGrams: number, gwp: number): number {
  return (chargeGrams * gwp) / 1_000_000;
}

const EUROS = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

/**
 * Montant en centimes → « 1 234,56 € », le seul formateur de montants (écrans,
 * PDF, messages) : espace fine insécable (U+202F) entre les milliers, insécable
 * (U+00A0) avant le symbole, comme le rend `Intl` en français.
 */
export function formatEuros(cents: Cents): string {
  return EUROS.format(cents / 100);
}

/**
 * Le même montant pour les e-mails et SMS : U+00A0 partout, car des messageries
 * et polices affichent mal l'espace fine (U+202F) — d'où « 1 234,56 € ».
 */
export function formatEurosNbsp(cents: Cents): string {
  return formatEuros(cents).replaceAll('\u202f', '\u00a0');
}

export function formatQty(milli: Milli): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(milli / 1000);
}
